import { useEffect, useRef, useState } from "react";
import animatedLogo from "@/assets/images/animated_logo.gif";
import AiIcon from "@/assets/svg/Ai-Icon";
import {
  CAMPAIGN_CONTENT_DONE_FLASH_MS,
  CAMPAIGN_CONTENT_DONE_MESSAGE,
  CAMPAIGN_CONTENT_GENERATING_MESSAGES,
  CAMPAIGN_CONTENT_GENERATING_TITLE,
  CAMPAIGN_CONTENT_MAX_PROGRESS_WHILE_GENERATING,
  CAMPAIGN_CONTENT_MESSAGE_INTERVAL_MS,
} from "@/features/campaigns/constants/campaignContentGeneratingMessages";

const PREPARING_SEQUENCE_MESSAGE = "Preparing your sequence…";

const CampaignContentGeneratingLoader = ({
  isGenerating,
  contentDone = 0,
  contentTotal = 0,
  percent = 0,
  onComplete,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isDonePhase, setIsDonePhase] = useState(false);
  const wasGeneratingRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onCompleteCalledRef = useRef(false);
  onCompleteRef.current = onComplete;

  const displayPercent = isDonePhase
    ? 100
    : Math.min(
        Math.max(percent, 0),
        isGenerating ? CAMPAIGN_CONTENT_MAX_PROGRESS_WHILE_GENERATING : 100,
      );

  useEffect(() => {
    if (isGenerating) {
      wasGeneratingRef.current = true;
      onCompleteCalledRef.current = false;
      setIsDonePhase(false);
      setMessageIndex(0);
      return undefined;
    }

    if (!wasGeneratingRef.current) return undefined;

    setIsDonePhase(true);

    const timeout = setTimeout(() => {
      wasGeneratingRef.current = false;
      setIsDonePhase(false);
      if (!onCompleteCalledRef.current) {
        onCompleteCalledRef.current = true;
        onCompleteRef.current?.();
      }
    }, CAMPAIGN_CONTENT_DONE_FLASH_MS);

    return () => clearTimeout(timeout);
  }, [isGenerating]);

  useEffect(() => {
    if (!isGenerating || isDonePhase) return undefined;

    const interval = setInterval(() => {
      setMessageIndex((prev) =>
        Math.min(prev + 1, CAMPAIGN_CONTENT_GENERATING_MESSAGES.length - 1),
      );
    }, CAMPAIGN_CONTENT_MESSAGE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isGenerating, isDonePhase]);

  const displayMessage = isDonePhase
    ? CAMPAIGN_CONTENT_DONE_MESSAGE
    : CAMPAIGN_CONTENT_GENERATING_MESSAGES[messageIndex];

  const safeTotal = Number(contentTotal || 0);
  const safeContentDone = Math.min(Number(contentDone || 0), safeTotal);
  const showStepProgress = !isDonePhase && safeTotal > 0;
  const showPreparingCopy = !isDonePhase && safeTotal <= 0 && isGenerating;

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] h-full w-full px-6 text-center">
      <img src={animatedLogo} alt="SalesWorx" className="w-20 h-20 mb-4" />

      <div className="flex items-center justify-center gap-2 mb-2">
        <AiIcon color="#207C97" />
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {CAMPAIGN_CONTENT_GENERATING_TITLE}
        </h3>
      </div>

      <p
        key={isDonePhase ? "done" : messageIndex}
        className="text-sm font-medium text-gray-600 dark:text-gray-300 animate-fade-in-up min-h-[2.5rem] max-w-md"
      >
        {displayMessage}
      </p>

      <div className="mt-4 w-full max-w-xs">
        <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
          <div
            className="h-full rounded-full bg-secondary transition-[width] duration-700 ease-out"
            style={{ width: `${Math.round(displayPercent)}%` }}
          />
        </div>
        {showStepProgress ? (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Content: {safeContentDone}/{safeTotal} complete ·{" "}
            {Math.round(displayPercent)}%
          </p>
        ) : null}
        {showPreparingCopy ? (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{PREPARING_SEQUENCE_MESSAGE}</p>
        ) : null}
      </div>
    </div>
  );
};

export default CampaignContentGeneratingLoader;
